import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { traiComplianceService, A2pCategory, CategoryDefaults } from 'api/services/traiCompliance.service';
import { voiceCampaignsService, VoiceCampaign } from 'api/services/voiceCampaigns.service';
import { aiAgentsService, AiAgent } from 'api/services/aiAgents.service';
import { CallCategorySelect } from 'components/Compliance/CallCategoryField';
import { CATEGORY_ADMINS, hasRole } from './traiRules';
import { Card, Note, muted, th, td } from './shared';

const selectCls = 'tl-input';

/**
 * Item 1: what automated calls are for. Resolution: the request's own category, then the
 * campaign's, then the agent's, then the organisation default, then unclassified — which is
 * what every call is today. Unset everywhere = exactly today's behaviour.
 */
const CategoriesTab: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const canEdit = hasRole(role, CATEGORY_ADMINS);

  const [data, setData] = useState<CategoryDefaults | null>(null);
  const [campaigns, setCampaigns] = useState<VoiceCampaign[]>([]);
  const [agents, setAgents] = useState<AiAgent[]>([]);
  const [orgValue, setOrgValue] = useState<'' | A2pCategory>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await traiComplianceService.categories();
    if (r.success && r.data) { setData(r.data); setOrgValue((r.data.orgDefault || '') as '' | A2pCategory); }
    else setNote({ ok: false, text: r.error || 'Could not load call categories' });
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    // Names only; a failure leaves ids, which still work.
    voiceCampaignsService.list().then((r) => { if (r.success) setCampaigns(r.data || []); });
    aiAgentsService.list().then((r) => { if (r.success) setAgents(r.data || []); });
  }, [load]);

  const save = async (key: string, op: () => Promise<{ success: boolean; error?: string }>) => {
    setBusy(key); setNote(null);
    const r = await op();
    setBusy(null);
    if (r.success) { setNote({ ok: true, text: 'Saved' }); load(); }
    else setNote({ ok: false, text: r.error || 'Could not save' });
  };

  if (loading && !data) return <div className="text-sm" style={muted}>Loading…</div>;

  const campaignValue = (id: string) => (data?.campaigns?.[id] || '') as '' | A2pCategory;
  const agentValue = (id: string) => (data?.agents?.[id] || '') as '' | A2pCategory;

  return (
    <div className="space-y-4">
      <Note note={note} />
      <Card title="Organisation default"
            subtitle="Used when neither the call, its campaign nor its agent says. Leave unset to keep today's behaviour."
            right={<button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={load}><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>}>
        <div className="flex items-center gap-2">
          <CallCategorySelect value={orgValue} onChange={setOrgValue} className={selectCls} />
          {canEdit && (
            <button className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50"
                    disabled={busy === 'org' || orgValue === (data?.orgDefault || '')}
                    onClick={() => save('org', () => traiComplianceService.setOrgCategory(orgValue || null))}>
              {busy === 'org' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
            </button>
          )}
        </div>
        <p className="text-xs" style={muted}>Order: {(data?.resolutionOrder || []).join(' → ')}</p>
      </Card>

      <Card title="Voice campaigns" subtitle="A campaign's category applies to every call it places.">
        {campaigns.length === 0 ? <p className="text-sm" style={muted}>No campaigns.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className={th}>Campaign</th><th className={th}>Status</th><th className={th}>Category</th></tr></thead>
              <tbody>
                {campaigns.filter((c) => c.id).map((c) => (
                  <CategoryRow key={c.id} label={c.campaignName || c.id!} meta={c.status || ''}
                               value={campaignValue(c.id!)} canEdit={canEdit} busy={busy === `c:${c.id}`}
                               onSave={(v) => save(`c:${c.id}`, () => traiComplianceService.setCampaignCategory(c.id!, v || null))} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="AI agents" subtitle="An agent's category applies to calls it places when the campaign or request does not say.">
        {agents.length === 0 ? <p className="text-sm" style={muted}>No agents.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className={th}>Agent</th><th className={th}>Id</th><th className={th}>Category</th></tr></thead>
              <tbody>
                {agents.filter((a) => a.id != null).map((a) => (
                  <CategoryRow key={a.id} label={a.name} meta={String(a.id)}
                               value={agentValue(String(a.id))} canEdit={canEdit} busy={busy === `a:${a.id}`}
                               onSave={(v) => save(`a:${a.id}`, () => traiComplianceService.setAgentCategory(a.id!, v || null))} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

const CategoryRow: React.FC<{
  label: string; meta: string; value: '' | A2pCategory; canEdit: boolean; busy: boolean;
  onSave: (v: '' | A2pCategory) => void;
}> = ({ label, meta, value, canEdit, busy, onSave }) => {
  const [v, setV] = useState<'' | A2pCategory>(value);
  useEffect(() => setV(value), [value]);
  return (
    <tr className="border-t border-gray-100 dark:border-gray-800">
      <td className={td}>{label}</td>
      <td className={td} style={muted}>{meta}</td>
      <td className={td}>
        <div className="flex items-center gap-2">
          <CallCategorySelect value={v} onChange={setV} className={selectCls} />
          {canEdit && (
            <button className="tl-btn tl-btn-secondary tl-btn-sm disabled:opacity-50" disabled={busy || v === value} onClick={() => onSave(v)}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default CategoriesTab;
